import { useEffect, useRef, useState } from 'react';

// Narrow canvas: the mockup is shown in a ~30rem column, so a smaller design width keeps the
// scale near 1 and the text readable (the old 782px canvas rendered 11px labels at about 6px).
const CONTENT_WIDTH = 540;
const CONTENT_HEIGHT = 700;
const CHROME_H = 44;
const NAVBAR_H = 56;
const BODY_H = CONTENT_HEIGHT - CHROME_H - NAVBAR_H;

const UPCOMING_SESSIONS = [
  { date: 'Tomorrow, 3:00 PM', subject: 'Algebra', tutor: 'Sarah T.' },
  { date: 'Friday, 4:00 PM', subject: 'SAT Prep', tutor: 'Sarah T.' },
];

export function ParentDashboardMockup() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.6);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / CONTENT_WIDTH);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div>
      <div>
        <div
          ref={containerRef}
          className="w-full rounded-xl overflow-hidden shadow-2xl border border-border"
          style={{ aspectRatio: `${CONTENT_WIDTH} / ${CONTENT_HEIGHT}` }}
        >
          <div
            style={{
              width: `${CONTENT_WIDTH}px`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              height: `${CONTENT_HEIGHT}px`,
            }}
          >
            {/* Browser chrome */}
            <div className="flex items-center gap-3 px-4 py-2.5 bg-muted dark:bg-zinc-900 border-b border-border" style={{ height: `${CHROME_H}px` }}>
              <div className="flex gap-1.5 shrink-0">
                <span className="h-3 w-3 rounded-full bg-red-500/80" />
                <span className="h-3 w-3 rounded-full bg-yellow-500/80" />
                <span className="h-3 w-3 rounded-full bg-green-500/80" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="bg-background dark:bg-zinc-800 border border-border rounded-md px-4 py-1 text-[11px] text-muted-foreground w-52 text-center">
                  mentaratutors.com/parent
                </div>
              </div>
            </div>

            {/* In-app Navbar */}
            <div
              className="flex items-center justify-between px-6 bg-background border-b border-border"
              style={{ height: `${NAVBAR_H}px` }}
            >
              <span className="text-xl font-bold tracking-tight text-rose-900 dark:text-rose-300">
                Mentara
              </span>
              <div className="flex h-8 w-8 rounded-full bg-violet-100 dark:bg-violet-900/30 items-center justify-center text-[12px] font-semibold text-violet-700 dark:text-violet-300">
                MP
              </div>
            </div>

            {/* Dashboard body */}
            <div
              className="bg-background text-foreground overflow-hidden"
              style={{ height: `${BODY_H}px` }}
            >
              <div
                className="px-6 py-5 space-y-4"
              >
                {/* Header */}
                <div>
                  <p className="text-2xl font-bold tracking-tight">Hi, Maria</p>
                  <p className="text-sm text-muted-foreground">Following: Alex M.</p>
                </div>

                {/* Summary row */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: '8', label: 'Sessions this month' },
                    { value: '7', label: 'Feedback received' },
                    { value: 'Tomorrow', label: 'Next session' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="rounded-lg border border-border bg-muted/30 px-2 py-3 text-center"
                    >
                      <p className="text-lg font-bold text-foreground">{stat.value}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Upcoming Sessions */}
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-sm font-semibold">Upcoming Sessions</p>
                  </div>
                  <div className="p-3 space-y-2">
                    {UPCOMING_SESSIONS.map((s, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2.5"
                      >
                        <div>
                          <p className="text-xs text-muted-foreground">{s.date}</p>
                          <p className="text-sm font-medium">
                            {s.subject} with {s.tutor}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                            Confirmed
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Feedback */}
                <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-2">
                  <p className="text-sm font-semibold">Recent Feedback from Tutor</p>
                  <blockquote className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-3 italic">
                    "Alex did great work on quadratic equations today. Still working on word
                    problems. We'll focus more next session."
                  </blockquote>
                  <p className="text-xs text-muted-foreground">
                    Sarah T. &nbsp;·&nbsp; 2 days ago
                  </p>
                </div>

                {/* Pending items */}
                <div className="rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20 px-4 py-3">
                  <p className="text-sm font-semibold">Pending items</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Statistics · tutor proposed Wednesday at 3:00 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
